import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  BarChart3, 
  Package, 
  Image, 
  FileText, 
  Settings, 
  Users, 
  Activity,
  Clock,
  TrendingUp,
  Download,
  Upload,
  Eye,
  LogOut,
  Home,
  Shield,
  Building,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  Server
} from 'lucide-react'

const AdminDashboard = () => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    totalProducts: 0,
    publishedProducts: 0,
    totalDownloads: 0,
    totalUsers: 1,
    activeCarousels: 0,
    totalFiles: 0,
    systemHealth: 'healthy',
    storageInfo: { used: '-', total: '-', diskUsagePercent: 0 },
    todayOps: 0,
    monthOps: 0
  })
  const [recentActivities, setRecentActivities] = useState([])
  const navigate = useNavigate()

  useEffect(() => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    
    if (!token || !userData) {
      // 未登录，跳转到登录页
      navigate('/admin/login')
      return
    }

    try {
      const parsedUser = JSON.parse(userData)
      setUser(parsedUser)
      loadDashboardData()
    } catch (error) {
      console.error('解析用户数据失败:', error)
      navigate('/admin/login')
    } finally {
      setLoading(false)
    }
  }, [navigate])

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      
      const statsResponse = await fetch('/api/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!statsResponse.ok) {
        if (statsResponse.status === 401) {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          navigate('/admin/login')
          return
        }
        throw new Error(`HTTP ${statsResponse.status}`)
      }

      const statsData = await statsResponse.json()
      if (statsData.success) {
        const d = statsData.data
        setStats({
          totalProducts: d.totalProducts || 0,
          publishedProducts: d.publishedProducts || 0,
          totalDownloads: d.totalDownloads || 0,
          totalUsers: d.totalUsers || 0,
          activeCarousels: d.activeCarousels || 0,
          totalFiles: d.totalFiles || 0,
          systemHealth: d.systemHealth || 'healthy',
          storageInfo: d.storageInfo || { used: '-', total: '-', diskUsagePercent: 0 },
          todayOps: d.todayOps || 0,
          monthOps: d.monthOps || 0
        })
        setRecentActivities(d.recentActivities || [])
      }
    } catch (error) {
      console.error('加载仪表板数据失败:', error)
      setError(error.message)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    navigate('/admin/login')
  }

  const handleNavigation = (path) => {
    navigate(path)
  }

  // 快速操作处理函数
  const handleQuickAction = (action) => {
    switch (action) {
      case 'uploadFile':
        navigate('/admin/files')
        break
      case 'addProduct':
        navigate('/admin/products')
        break
      case 'manageCarousel':
        navigate('/admin/carousel')
        break
      case 'manageCompany':
        navigate('/admin/company')
        break
      case 'systemSettings':
        navigate('/admin/settings')
        break
      default:
        break
    }
  }

  // 获取系统状态描述
  const getSystemStatusText = (status) => {
    switch (status) {
      case 'healthy': return '系统运行正常'
      case 'warning': return '系统存在警告'
      case 'error': return '系统出现异常'
      default: return '系统状态未知'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100'
      case 'warning': return 'text-yellow-600 bg-yellow-100'
      case 'error': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('zh-CN')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-gray-900">
                后台管理系统
              </h1>
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 flex items-center space-x-1 text-sm"
              >
                <Home size={16} />
                <span>返回首页</span>
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Shield className="text-blue-500" size={20} />
                <span className="text-gray-700 font-medium">
                  {user?.display_name || user?.username}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {user?.role === 'super_admin' ? '超级管理员' : '内容管理员'}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
              >
                <LogOut size={16} />
                <span>退出登录</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* 欢迎横幅 */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-2">
                  欢迎回来，{user?.display_name || user?.username}！
                </h1>
                <p className="text-blue-100">
                  无锡市摩恒科技有限公司后台管理系统 - 今天是 {new Date().toLocaleDateString('zh-CN')}
                </p>
              </div>
              <div className="text-right">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(stats.systemHealth)}`}>
                  <Activity size={14} className="mr-1" />
                  系统状态: {stats.systemHealth === 'healthy' ? '正常' : '异常'}
                </div>
              </div>
            </div>
          </div>

          {/* 统计卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">产品总数</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
                  <p className="text-xs text-green-600 mt-1">{stats.publishedProducts} 已发布</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Package className="text-blue-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">总下载量</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalDownloads.toLocaleString()}</p>
                  <p className="text-xs text-green-600 mt-1">文件资源下载</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Download className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">文件/轮播</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalFiles} / {stats.activeCarousels}</p>
                  <p className="text-xs text-gray-500 mt-1">文件 | 活跃轮播</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FileText className="text-purple-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">磁盘使用</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.storageInfo.used}</p>
                  <p className={`text-xs mt-1 ${stats.storageInfo.diskUsagePercent > 90 ? 'text-red-600' : stats.storageInfo.diskUsagePercent > 70 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {stats.storageInfo.diskUsagePercent}% 已用
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${stats.storageInfo.diskUsagePercent > 90 ? 'bg-red-100' : stats.storageInfo.diskUsagePercent > 70 ? 'bg-yellow-100' : 'bg-orange-100'}`}>
                  <HardDrive className={stats.storageInfo.diskUsagePercent > 90 ? 'text-red-600' : stats.storageInfo.diskUsagePercent > 70 ? 'text-yellow-600' : 'text-orange-600'} size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* 系统状态 & 存储详情 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Server className="mr-2" size={20} />
                系统状态
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">系统运行状态</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stats.systemHealth === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {stats.systemHealth === 'healthy' ? <CheckCircle size={12} className="mr-1" /> : <AlertTriangle size={12} className="mr-1" />}
                    {stats.systemHealth === 'healthy' ? '正常' : '异常'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">管理员账号</span>
                  <span className="text-sm font-medium text-gray-900">{stats.totalUsers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">今日操作记录</span>
                  <span className="text-sm font-medium text-gray-900">{stats.todayOps}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">本月操作记录</span>
                  <span className="text-sm font-medium text-gray-900">{stats.monthOps}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <HardDrive className="mr-2" size={20} />
                存储空间
              </h3>
              <div className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">已用空间</span>
                  <span className="font-medium">{stats.storageInfo.used} / {stats.storageInfo.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-500 ${
                      stats.storageInfo.diskUsagePercent > 90 ? 'bg-red-500' : 
                      stats.storageInfo.diskUsagePercent > 70 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(stats.storageInfo.diskUsagePercent, 100)}%` }}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.storageInfo.diskUsagePercent > 90 
                  ? '⚠️ 磁盘空间即将耗尽，请及时清理' 
                  : stats.storageInfo.diskUsagePercent > 70 
                  ? '⚡ 磁盘使用率较高，建议关注' 
                  : '✅ 磁盘空间充足'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 功能模块 */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <BarChart3 className="mr-2" size={20} />
                    功能模块
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => handleNavigation('/admin/products')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="p-3 bg-blue-100 rounded-lg mr-4">
                        <Package className="text-blue-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">产品管理</h3>
                        <p className="text-sm text-gray-500">管理公司产品信息</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleNavigation('/admin/carousel')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="p-3 bg-green-100 rounded-lg mr-4">
                        <Image className="text-green-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">轮播图管理</h3>
                        <p className="text-sm text-gray-500">管理首页轮播图</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleNavigation('/admin/files')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="p-3 bg-purple-100 rounded-lg mr-4">
                        <FileText className="text-purple-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">技术支持</h3>
                        <p className="text-sm text-gray-500">管理技术支持资源</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleNavigation('/admin/company')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="p-3 bg-indigo-100 rounded-lg mr-4">
                        <Building className="text-indigo-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">公司简介管理</h3>
                        <p className="text-sm text-gray-500">管理公司介绍内容</p>
                      </div>
                    </button>

                    <button
                      onClick={() => handleNavigation('/admin/settings')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="p-3 bg-orange-100 rounded-lg mr-4">
                        <Settings className="text-orange-600" size={20} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">系统设置</h3>
                        <p className="text-sm text-gray-500">管理系统配置</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 最近活动 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Clock className="mr-2" size={20} />
                    最近活动
                  </h2>
                </div>
                <div className="p-4">
                  {recentActivities.length > 0 ? (
                    <div className="space-y-4">
                      {recentActivities.slice(0, 5).map((activity, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className={`flex-shrink-0 w-2 h-2 mt-2 rounded-full ${
                            activity.status === 'success' ? 'bg-green-500' : 
                            activity.status === 'error' ? 'bg-red-500' : 'bg-yellow-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {activity.description}
                            </p>
                            <p className="text-xs text-gray-500">
                              {activity.adminName} • {formatTime(activity.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Clock className="mx-auto text-gray-400" size={32} />
                      <p className="mt-2 text-sm text-gray-500">暂无活动记录</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 快速操作 */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">快速操作</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <button 
                  onClick={() => handleQuickAction('uploadFile')}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors hover:shadow-sm"
                >
                  <Upload className="text-blue-600 mb-2" size={24} />
                  <span className="text-sm font-medium">技术支持</span>
                  <span className="text-xs text-gray-500 mt-1">管理下载资源</span>
                </button>
                <button 
                  onClick={() => handleQuickAction('addProduct')}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors hover:shadow-sm"
                >
                  <Package className="text-green-600 mb-2" size={24} />
                  <span className="text-sm font-medium">添加产品</span>
                  <span className="text-xs text-gray-500 mt-1">发布新产品</span>
                </button>
                <button 
                  onClick={() => handleQuickAction('manageCarousel')}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors hover:shadow-sm"
                >
                  <Image className="text-purple-600 mb-2" size={24} />
                  <span className="text-sm font-medium">管理轮播图</span>
                  <span className="text-xs text-gray-500 mt-1">首页展示设置</span>
                </button>
                <button 
                  onClick={() => handleQuickAction('manageCompany')}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors hover:shadow-sm"
                >
                  <Building className="text-indigo-600 mb-2" size={24} />
                  <span className="text-sm font-medium">公司简介</span>
                  <span className="text-xs text-gray-500 mt-1">管理公司介绍</span>
                </button>
                <button 
                  onClick={() => handleQuickAction('systemSettings')}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors hover:shadow-sm"
                >
                  <Settings className="text-orange-600 mb-2" size={24} />
                  <span className="text-sm font-medium">系统设置</span>
                  <span className="text-xs text-gray-500 mt-1">配置系统参数</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
